import {
  type ButtonHTMLAttributes,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Icon from './Icon';
import type { IconName } from './icons';
import styles from './Menu.module.css';

/** The props the trigger must spread onto its own button. */
export interface MenuTriggerProps {
  ref: Ref<HTMLButtonElement>;
  onClick: () => void;
  'aria-expanded': boolean;
  'aria-haspopup': 'menu';
}

export interface MenuProps {
  /**
   * Renders the button that opens the menu, and must spread the props it is
   * given. A render prop rather than a cloned child so that the ref and the ARIA
   * state are visible at the call site: `cloneElement` puts them on whatever
   * element happens to be on top, which silently does the wrong thing the
   * moment someone wraps the trigger in a tooltip or a div.
   */
  trigger: (props: MenuTriggerProps) => ReactNode;
  children: ReactNode;
  /** Which edge the menu lines up with. `end` for a menu opened from the right
      side of a container, so it does not run off the edge. */
  align?: 'start' | 'end';
  className?: string;
}

const CLOSE_MS = 155; // keep in step with --ui-dur-3-out

/**
 * A dropdown menu of commands.
 *
 * Menus hold *actions* ("Rename", "Delete"). For choosing a value, use `Select`:
 * a menu has no selected state to show once it closes, so a value chosen from
 * one is invisible afterwards.
 *
 * Keyboard behaviour is the part worth knowing, because it is what makes a menu
 * a menu rather than a floating div: arrow keys move between items, Home and End
 * jump to the ends, Escape closes and puts focus back on the trigger, and Tab
 * closes rather than walking into the menu. Opening moves focus to the first
 * item, so a keyboard user is never left with focus on a trigger whose menu is
 * open behind them.
 */
export default function Menu({ trigger, children, align = 'start', className }: MenuProps) {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const close = useCallback((returnFocus: boolean) => {
    setClosing(true);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  // The exit animation needs the element to stay mounted while it plays. A
  // timeout rather than `animationend` because a reduced-motion user has the
  // animation collapsed to 0.01ms, and some browsers do not fire the event at
  // all when it is that short.
  useEffect(() => {
    if (!closing) return;
    const t = setTimeout(() => {
      setClosing(false);
      setOpen(false);
    }, CLOSE_MS);
    return () => clearTimeout(t);
  }, [closing]);

  useEffect(() => {
    if (!open || closing) return;

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      close(false);
    };
    // `pointerdown` rather than `click`: a click that starts inside the menu and
    // ends outside it is not a dismissal, and listening for click makes a
    // text selection drag close the menu.
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, closing, close]);

  useEffect(() => {
    if (!open || closing) return;
    const items = menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]');
    items?.[0]?.focus();
  }, [open, closing]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? [],
    );
    if (items.length === 0) return;
    const index = items.indexOf(document.activeElement as HTMLElement);

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        close(true);
        break;
      case 'ArrowDown':
        e.preventDefault();
        items[(index + 1) % items.length]?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        items[(index - 1 + items.length) % items.length]?.focus();
        break;
      case 'Home':
        e.preventDefault();
        items[0]?.focus();
        break;
      case 'End':
        e.preventDefault();
        items[items.length - 1]?.focus();
        break;
      case 'Tab':
        // Not prevented: tabbing out of a menu should land wherever the page
        // would have taken you, not trap you inside it.
        close(false);
        break;
      default:
        break;
    }
  };

  return (
    <div className={[styles.root, className ?? ''].filter(Boolean).join(' ')}>
      {trigger({
        ref: triggerRef,
        onClick: () => (open ? close(false) : setOpen(true)),
        'aria-expanded': open,
        'aria-haspopup': 'menu',
      })}
      {open ? (
        <div
          ref={menuRef}
          role="menu"
          className={[styles.menu, styles[align], closing ? styles.closing : styles.opening].join(
            ' ',
          )}
          onKeyDown={onKeyDown}
          // Any activation closes the menu. Listening on the container rather
          // than wiring every item keeps MenuItem free of close plumbing.
          onClick={() => close(true)}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

export interface MenuItemProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onSelect'> {
  children: ReactNode;
  icon?: IconName;
  /** Marks the item as the current choice. For a menu that reflects state, like
      a theme picker. */
  checked?: boolean;
  /** Irreversible. Washes red on hover. */
  danger?: boolean;
  onSelect?: () => void;
}

export function MenuItem({
  children,
  icon,
  checked = false,
  danger = false,
  onSelect,
  className,
  ...rest
}: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      className={[styles.item, danger ? styles.danger : '', className ?? '']
        .filter(Boolean)
        .join(' ')}
      onClick={onSelect}
      {...rest}
    >
      {icon ? <Icon name={icon} size={15} /> : null}
      <span className={styles.itemLabel}>{children}</span>
      {checked ? <Icon name="check" size={13} className={styles.check} /> : null}
    </button>
  );
}

/** A rule between groups of items. An `<hr>` rather than a div with
    `role="separator"`, because the ARIA separator role is focusable by default
    and would land in the arrow-key path. */
export function MenuSeparator() {
  return <hr className={styles.separator} />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <div className={styles.groupLabel}>{children}</div>;
}
